// src/pages/ServiceProviders.tsx (Enhanced with Edit/Delete)
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Container, 
  Grid, 
  Alert, 
  CircularProgress, 
  Box, 
  Chip, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon, Star, Diamond } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import MessageButton from '../components/MessageButton';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/api';

interface ServiceProvider {
  id: number;
  businessName: string;
  description: string;
  services: string[];
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  serviceArea: string;
  operatingHours: string;
  status: string;
  claimedByUserId?: number;
  isPremium?: boolean;
  isFeatured?: boolean;
  premiumExpiresAt?: string;
}

const ServiceProviders: React.FC = () => {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialog, setEditDialog] = useState<{ open: boolean; provider: ServiceProvider | null }>({ open: false, provider: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; provider: ServiceProvider | null }>({ open: false, provider: null });
  const [editData, setEditData] = useState({
    businessName: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    description: '',
    services: '',
    serviceArea: '',
    operatingHours: '',
    status: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchProviders = async () => {
    try {
      const url = `${API_BASE_URL}/providers`;
      console.log('[ServiceProviders] Fetching from:', url);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      
      // Sort providers: Platinum first, then Premium, then Basic
      const sortedProviders = [...data].sort((a: ServiceProvider, b: ServiceProvider) => {
        if (a.isFeatured && !b.isFeatured) return -1;
        if (!a.isFeatured && b.isFeatured) return 1;
        if (a.isPremium && !b.isPremium) return -1;
        if (!a.isPremium && b.isPremium) return 1;
        return 0;
      });
      
      setProviders(sortedProviders);
    } catch (err: any) {
      console.error('[ServiceProviders] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const handleEdit = (provider: ServiceProvider) => {
    setEditData({
      businessName: provider.businessName,
      contactPerson: provider.contactPerson,
      phone: provider.phone,
      email: provider.email,
      address: provider.address,
      description: provider.description,
      services: provider.services.join(', '),
      serviceArea: provider.serviceArea,
      operatingHours: provider.operatingHours,
      status: provider.status
    });
    setEditDialog({ open: true, provider });
  };

  const handleDelete = (provider: ServiceProvider) => {
    setDeleteDialog({ open: true, provider });
  };

  const handleEditSubmit = async () => {
    if (!editDialog.provider) return;
    
    try {
      const servicesArray = editData.services.split(',').map(s => s.trim());
      const response = await fetch(`${API_BASE_URL}/providers/${editDialog.provider.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editData,
          services: servicesArray
        }),
      });
      
      if (!response.ok) throw new Error('Failed to update service provider');
      
      setMessage({ type: 'success', text: 'Service provider updated successfully!' });
      setEditDialog({ open: false, provider: null });
      fetchProviders(); // Refresh the list
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update service provider' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.provider) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/providers/${deleteDialog.provider.id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete service provider');
      
      setMessage({ type: 'success', text: 'Service provider deleted successfully!' });
      setDeleteDialog({ open: false, provider: null });
      fetchProviders(); // Refresh the list
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete service provider' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Typography variant="h4" component="h1" gutterBottom>
        Service Providers
      </Typography>
      
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {providers.length > 0 ? (
          providers.map((pro) => (
            <Grid item xs={12} md={6} key={pro.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  border: pro.isFeatured ? '2px solid #ffd700' : pro.isPremium ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  boxShadow: pro.isFeatured ? '0 4px 20px rgba(255, 215, 0, 0.3)' : pro.isPremium ? '0 4px 15px rgba(25, 118, 210, 0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: pro.isFeatured ? '0 8px 30px rgba(255, 215, 0, 0.4)' : pro.isPremium ? '0 8px 25px rgba(25, 118, 210, 0.3)' : '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CardContent sx={{ flex: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                          {pro.businessName}
                        </Typography>
                        {pro.isFeatured && (
                          <Chip 
                            icon={<Diamond sx={{ color: '#ffd700' }} />}
                            label="Platinum" 
                            size="small"
                            sx={{ 
                              backgroundColor: '#ffd700', 
                              color: '#000',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />
                        )}
                        {pro.isPremium && !pro.isFeatured && (
                          <Chip 
                            icon={<Star sx={{ color: '#1976d2' }} />}
                            label="Premium" 
                            size="small"
                            sx={{ 
                              backgroundColor: '#1976d2', 
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.7rem'
                            }} 
                          />
                        )}
                      </Box>
                    </Box>
                    {user?.role === 'admin' && (
                      <Box>
                        <IconButton size="small" onClick={() => handleEdit(pro)} sx={{ color: '#1976d2' }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(pro)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  
                  <Typography sx={{ mb: 2, color: '#666', fontSize: '1rem' }}>
                    <strong>Contact Person:</strong> {pro.contactPerson}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: '#444' }}>
                    {pro.description}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2c3e50' }}>
                      Services Offered:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {pro.services.map(service => (
                        <Chip 
                          key={service} 
                          label={service} 
                          size="small"
                          sx={{ 
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: '#bbdefb'
                            }
                          }} 
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    backgroundColor: '#f8f9fa', 
                    p: 2, 
                    borderRadius: 1, 
                    border: '1px solid #e9ecef',
                    mb: 2
                  }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      📞 <strong>Phone:</strong> {pro.phone}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      ✉️ <strong>Email:</strong> {pro.email}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      📍 <strong>Service Area:</strong> {pro.serviceArea}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      🕒 <strong>Hours:</strong> {pro.operatingHours}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                    <Chip 
                      label={pro.status} 
                      color={pro.status === 'approved' ? 'success' : pro.status === 'pending' ? 'warning' : 'error'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      ID: {pro.id}
                    </Typography>
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => navigate(`/providers/${pro.id}`)}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      View Details
                    </Button>
                    {pro.claimedByUserId ? (
                      <MessageButton
                        receiverId={pro.claimedByUserId}
                        receiverName={pro.businessName}
                        variant="contained"
                        size="small"
                      />
                    ) : (
                      <Tooltip title="Messaging available once the provider claims their profile">
                        <span>
                          <Button variant="contained" size="small" disabled>
                            Message
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Typography>No service providers found.</Typography>
        )}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, provider: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit Service Provider</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Business Name"
                fullWidth
                value={editData.businessName}
                onChange={(e) => setEditData({ ...editData, businessName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                fullWidth
                value={editData.contactPerson}
                onChange={(e) => setEditData({ ...editData, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                value={editData.address}
                onChange={(e) => setEditData({ ...editData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Services (comma-separated)"
                fullWidth
                placeholder="e.g., Plumbing, Electrical, HVAC"
                value={editData.services}
                onChange={(e) => setEditData({ ...editData, services: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Service Area"
                fullWidth
                value={editData.serviceArea}
                onChange={(e) => setEditData({ ...editData, serviceArea: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Operating Hours"
                fullWidth
                placeholder="e.g., Monday-Friday: 8:00 AM - 6:00 PM"
                value={editData.operatingHours}
                onChange={(e) => setEditData({ ...editData, operatingHours: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Status"
              fullWidth
              value={editData.status}
              onChange={(e) => setEditData({ ...editData, status: e.target.value })}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="declined">Declined</MenuItem>
            </TextField>
          </Grid>
            </Grid>
            
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, provider: null })}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, provider: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.provider?.businessName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, provider: null })}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ServiceProviders;